/* eslint-disable @typescript-eslint/no-explicit-any */

// ** React Imports
import { Fragment } from "react";

// ** Routes Imports
import AppRoutes from "./Apps";
import PagesRoutes from "./Pages";
import DashboardRoutes from "./Dashboards";
import ExtensionsRoutes from "./Extensions";
import AuthenticationRoutes from "./Authentication";

// ** Layouts
import VerticalLayout from "@src/layouts/VerticalLayout";
import LayoutWrapper from "@src/@core/layouts/components/layout-wrapper";
import PrivateRoute from "@src/@core/components/routes/PrivateRoute";
import PublicRoute from "@src/@core/components/routes/PublicRoute";
import BlankLayout from "@src/@core/layouts/BlankLayout";
import { isObjEmpty } from "@src/utility/Utils";
import type { RouteObject } from "react-router";


// ** Utils

// ** Types

// ** Document title
const TemplateTitle = "%s - Quila Beauty Admin";

// ** Default Route
const DefaultRoute = "/dashboard/analytics";

// ** Layout mapping
const getLayout: Record<string, JSX.Element> = {
  blank: <BlankLayout />,
  vertical: <VerticalLayout />,
};

// ** Merged all app routes
const Routes: RouteObject[] = [
  ...AuthenticationRoutes,
  ...DashboardRoutes,
  ...AppRoutes,
  ...PagesRoutes,
  ...ExtensionsRoutes,
];

// ** Extract meta if needed
const getRouteMeta = (route: any) => {
  if (isObjEmpty(route.element?.props)) {
    return route.meta ? { routeMeta: route.meta } : {};
  }
  return {};
};

// ** Merge layout-specific routes
const MergeLayoutRoutes = (
  layout: string,
  defaultLayout: string
): RouteObject[] => {
  const LayoutRoutes: RouteObject[] = [];

  Routes.forEach((route: any) => {
    let isBlank = false;

    const layoutMatches =
      (route.meta?.layout && route.meta.layout === layout) ||
      (!route.meta?.layout && defaultLayout === layout);

    if (layoutMatches) {
      let RouteTag = PrivateRoute;

      if (route.meta) {
        isBlank = route.meta.layout === "blank";
        RouteTag = route.meta.publicRoute ? PublicRoute : PrivateRoute;
      }

      if (route.element) {
        const Wrapper =
          isObjEmpty(route.element.props) && !isBlank
            ? LayoutWrapper
            : Fragment;
        const routeMeta = !isBlank ? getRouteMeta(route) : {};

        route.element = (
          <Wrapper {...routeMeta}>
            <RouteTag route={route}>{route.element}</RouteTag>
          </Wrapper>
        );
      }

      LayoutRoutes.push(route);
    }
  });

  return LayoutRoutes;
};

// ** Generate route tree with appropriate layout
const getRoutes = (layout?: string): RouteObject[] => {
  const defaultLayout = layout || "vertical";
  const layouts = ["vertical", "horizontal", "blank"];

  const AllRoutes: RouteObject[] = layouts.map((layoutItem) => ({
    path: "/",
    element: getLayout[layoutItem] || getLayout[defaultLayout],
    children: MergeLayoutRoutes(layoutItem, defaultLayout),
  }));
  return AllRoutes;
};

// eslint-disable-next-line react-refresh/only-export-components
export { DefaultRoute, TemplateTitle, Routes, getRoutes };
